import type { Candidate } from '@entities/candidates';
import { QueryKey } from '@shared/config';
import { useInfiniteRequest } from '@shared/config/query/hooks';
import { PaginateType } from '@shared/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { GetVacancyCandidatesRequest } from '../types';
import { vacancyApi } from '../vacancyApi';

type UseGetVacancyCandidatesParams = GetVacancyCandidatesRequest & {
  enabled?: boolean;
};

export const useGetVacancyCandidates = (
  vacancyId: number | undefined,
  params: UseGetVacancyCandidatesParams
) => {
  const { enabled = true, ...requestParams } = params;

  return useInfiniteRequest<Candidate>({
    queryKey: [QueryKey.GET_VACANCY_CANDIDATES, requestParams, vacancyId],
    queryFn: ({ pageParam }) => {
      if (vacancyId === undefined) {
        throw new Error('Vacancy ID is required to load candidates');
      }

      return vacancyApi.getVacancyCandidates(vacancyId, {
        ...requestParams,
        ...pageParam,
      });
    },
    initialPageParam: {
      perPage: requestParams.perPage,
      page: requestParams.page,
    },
    placeholderData: keepPreviousData,
    enabled: Boolean(vacancyId) && enabled,
  });
};

type UseGetAllVacancyCandidatesParams = Omit<
  GetVacancyCandidatesRequest,
  'paginate'
> & {
  enabled?: boolean;
};

export const useGetAllVacancyCandidates = (
  vacancyId: number | undefined,
  params: UseGetAllVacancyCandidatesParams
) => {
  const { enabled = true, ...requestParams } = params;
  const allCandidatesParams = {
    ...requestParams,
    paginate: PaginateType.NONE,
  } as const;

  return useQuery({
    queryKey: [QueryKey.GET_VACANCY_CANDIDATES, allCandidatesParams, vacancyId],
    queryFn: () => {
      if (vacancyId === undefined) {
        throw new Error('Vacancy ID is required to load candidates');
      }

      return vacancyApi.getVacancyCandidates(vacancyId, allCandidatesParams);
    },
    enabled: Boolean(vacancyId) && enabled,
    retry: false,
  });
};
